<template>
  <div class="w-full flex flex-col h-full overflow-y-auto">
    <div class="flex items-center justify-between px-4 sm:px-12 py-8">
      <div class="flex gap-x-6 items-center justify-between w-full">
        <h2 class="font-semibold text-blue-600 text-2xl">Locations</h2>
        <button
          type="button"
          @click="onClickManageBtn"
          class="btn btn-secondary-outline"
        >
          Manage
        </button>
      </div>
    </div>

    <AgGridVue
      v-bind="gridOptions"
      class="ag-theme-alpine salve-theme-package-locations flex-1"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { AgGridVue } from 'ag-grid-vue3'
import SpaceCell from '@/Components/DataGrid/Cells/SpaceCell.vue'
import DateCell from '@/Components/DataGrid/Cells/DateCell.vue'
import PackageStatus from '@/Components/DataGrid/Cells/PackageStatus.vue'
import spaceMovementPanel from '@/Pages/Packages/SpaceMovementPanel'
import spaceManagement from '@/Pages/Packages/SpaceManagement'
import { getHeadersWithToken } from '@/utils/http'
import { useAccessUser } from '@/Composables/access-user'
import { useGridApi } from '@/Components/DataGrid/grid-api'
import LinkCell from '@/Components/DataGrid/Cells/LinkCell.vue'

const { gridApi, createGridOptions } = useGridApi()

const locationsData = ref([])

const props = defineProps({
  name: {
    type: String,
    required: true
  },
  packageTitle: {
    type: String,
    required: true
  },
  packageId: {
    type: Number,
    required: true
  }
})

const { user } = useAccessUser()

onMounted(async () => {
  await spaceManagement.loadLocationsData(props.packageId, (data) => {
    locationsData.value = data
    gridApi.value.setGridOption('rowData', locationsData.value)
  })
})

spaceManagement.setPackage({
  id: props.packageId,
  title: props.packageTitle
})

spaceManagement.onUpdate((data) => {
  const index = locationsData.value.findIndex((location) => location.id === data.id)
  locationsData.value[index] = data

  gridApi.value.setGridOption('rowData', locationsData.value)

  spaceMovementPanel.close()
})

const detailCellRendererParams = (params) => {
  const location = locationsData.value.find((location) => location.id === params.data.id)

  if (!location) {
    throw new Error(`Location with id: ${params.data.id} not found`)
  }

  let salesFloorIndex = null
  const spaceCols = location.spaces.map((space, index) => {
    if (space.name === 'Sales Floor') {
      salesFloorIndex = index
    }
    return {
      headerName: space.name,
      cellRenderer: SpaceCell,
      cellRendererParams: {
        locationId: location.id,
        spaceId: space.id,
        showOnHand: user.value.system_role === 'admin'
      }
    }
  })

  if (salesFloorIndex && salesFloorIndex > 0) {
    let salesFloorCol = spaceCols.splice(salesFloorIndex, 1)[0]
    spaceCols.unshift(salesFloorCol)
  }

  return {
    detailGridOptions: {
      rowHeight: 34,

      columnDefs: [
        {
          headerName: 'Sub-Lot Number',
          field: 'sub_lot_num',
          width: 280,
          cellRenderer: LinkCell,
          cellRendererParams: (params) => ({
            url: route('integrations.biotrack.inventory.show', { source_id: params.value }),
            display: params.value,
            cellClass: params.data.is_disabled ? 'text-gray-600' : ''
          })
        },
        {
          headerName: 'Received Date',
          field: 'received_date',
          width: 220,
          cellRenderer: DateCell,
          cellRendererParams: {
            formatter: 'MM/DD/YYYY hh:mmA'
          }
        },
        {
          headerName: 'Transferred In',
          field: 'transferred_in',
          width: 220
        },
        {
          headerName: 'Stock On Hand',
          field: 'stock_on_hand',
          width: 220
        },
        {
          headerName: 'Available For Sale',
          field: 'available_for_sale',
          width: 220
        },
        ...spaceCols
      ],
      defaultColDef: {
        suppressHeaderMenuButton: true,
        sortable: false
      },
      suppressMovableColumns: true,
      getRowId: (params) => {
        return params.data.id
      }
    },
    getDetailRowData: (params) => {
      params.successCallback(params.data.sub_lots)
    }
  }
}

const gridOptions = createGridOptions({
  onGridReady: (params) => (gridApi.value = params.api),
  getRowId: (params) => params.data.id,

  columnDefs: [
    {
      headerName: 'Location',
      field: 'name',
      minWidth: 500,
      cellRenderer: 'agGroupCellRenderer',
      cellClass: 'text-blue-600 font-bold uppercase'
    },
    {
      headerName: 'Transferred In',
      field: 'transferred_in_qty',
      cellClass: 'font-semibold',
      cellRenderer: 'agAnimateShowChangeCellRenderer',
      width: 220
    },
    {
      headerName: 'Stock On Hand',
      field: 'stock_on_hand_qty',
      cellClass: 'font-semibold',
      cellRenderer: 'agAnimateShowChangeCellRenderer',
      width: 220
    },
    {
      headerName: 'Available For Sale',
      field: 'available_for_sale_qty',
      cellClass: 'font-semibold',
      cellRenderer: 'agAnimateShowChangeCellRenderer',
      width: 220
    },
    {
      headerName: 'Sales Floor',
      field: 'sales_floor_qty',
      cellClass: 'font-semibold',
      cellRenderer: 'agAnimateShowChangeCellRenderer',
      width: 220
    },
    {
      headerName: 'Ecom',
      field: 'ecom_enabled',
      cellRenderer: PackageStatus,
      cellRendererParams: (params) => ({
        submit: updateLocationSubmit('has_ecommerce', params.data.id)
      }),
      flex: 1
    }
  ],

  defaultColDef: {
    floatingFilter: true,
    suppressHeaderMenuButton: true
  },

  suppressMovableColumns: true,
  pagination: false,
  animateRows: false,
  sideBar: null,
  masterDetail: true,
  detailRowAutoHeight: true,
  detailCellRendererParams: detailCellRendererParams
})

const onClickManageBtn = () => {
  spaceManagement.initForm()
  spaceMovementPanel.open()
}

const updateLocationSubmit = (key, locationId) => {
  return async (toValue) => {
    const putResponse = await fetch(
      route('api.packages.locations.update', { package: props.packageId, location: locationId }),
      {
        headers: getHeadersWithToken(),
        method: 'put',
        body: JSON.stringify({ [key]: toValue })
      }
    )

    if (!putResponse.ok) {
      throw new Error('Failed to update package location data')
    }
  }
}
</script>
